import { HelpFeature } from '@/model/HelpModel';
import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Input, Select, Space, Spin, Tooltip, notification } from 'antd';
import cx from 'classnames';
import React, { CSSProperties, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { handleResponse } from '../../../Helper';
import { velocityVisionApi } from '../../../api';
import { SelectOptions } from '../../../model/CommonModel';
import { Logger } from '../../../model/LoggingModel';
import { Instance, VelocityVisionResponseObject } from '../../../model/VelocityVisionModel';
import { HelpFactoryComponent, Modal } from '../../common';
import { HighlightText } from '../../common/HighlightText/HighlightText';
import styles from './addEditModal.module.scss';

type Props = {
	isEdit?: boolean;
	title: string;
	editInstance?: Instance;
	onConfirm: () => void;
	onCancel?: () => void;
};

const nameInputId: string = 'addNewInstanceNameInput';
const urlInputId: string = 'addNewInstanceUrlInput';
const protocols: string[] = ['http://', 'https://'];
const maxPortNumber: number = 65535;

const hex = 'a-fA-F0-9';
const protocolPattern = 'https?:\\/\\/';
const hostNamePattern = '(([a-zA-Z0-9-]{1,63}\\.)+[a-zA-Z]{2,})|([a-zA-Z0-9-]+)';
const ipv4Pattern =
	'\\b(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.' +
	'(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.' +
	'(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.' +
	'(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\b';
const ipv6Pattern =
	`\\[(([${hex}]{1,4}:){7}[${hex}]{1,4}|` +
	`([${hex}]{1,4}:){1,7}:|` +
	`([${hex}]{1,4}:){1,6}:[${hex}]{1,4}|` +
	`([${hex}]{1,4}:){1,5}(:[${hex}]{1,4}){1,2}|` +
	`([${hex}]{1,4}:){1,4}(:[${hex}]{1,4}){1,3}|` +
	`([${hex}]{1,4}:){1,3}(:[${hex}]{1,4}){1,4}|` +
	`([${hex}]{1,4}:){1,2}(:[${hex}]{1,4}){1,5}|` +
	`[${hex}]{1,4}:(:[${hex}]{1,4}){1,6}|` +
	`::(ffff(:0{1,4}){0,1}:){0,1}` +
	`((([${hex}]{1,4}:){1,4}|([0-9]{1,3}\\.){3}[0-9]{1,3}))|` +
	`::(:[${hex}]{1,4}){1,7}|` +
	`::[${hex}]{1,4}|` +
	`[${hex}]{1,4}::|` +
	`::1|` +
	`::2|` +
	`[${hex}]{1,4}::1)\\]`;
const portPattern = '(:\\d{1,5})';
const pathPattern = '(\\/[^\\s\\?#]*)?(\\?[^\\s#]*)?(#\\S*)?';
const urlRootPattern = `(${protocolPattern})(${hostNamePattern}|${ipv4Pattern}|${ipv6Pattern})${portPattern}`;
const urlRegexFull = new RegExp(`^${urlRootPattern}(${pathPattern})?$`, 'i');
const urlRegexRoot = new RegExp(`^${urlRootPattern}$`, 'i');
const topicId: string = 'AddNewInstance';

const PortErrorStyle: CSSProperties = { color: 'red', fontWeight: 'bold', textDecoration: 'underline' };

const AddEditModal: React.FC<Props> = ({ editInstance = undefined, title, onConfirm, onCancel }) => {
	const [name, setName] = useState<string>('');
	const [url, setUrl] = useState<string>('');
	const [fullUrl, setFullUrl] = useState<string>('');
	const [rootUrl, setRootUrl] = useState<string>('');
	const [isNameEmpty, setIsNameEmpty] = useState<boolean>(false);
	const [isNameDuplicate, setIsNameDuplicate] = useState<boolean>(false);
	const [isUrlDuplicate, setIsUrlDuplicate] = useState<boolean>(false);
	const [isUrlValid, setIsUrlValid] = useState<boolean>(false);
	const [isUrlLong, setIsUrlLong] = useState<boolean>(false);
	const [hasNameBeenUsed, setHasNameBeenUsed] = useState<boolean>(false);
	const [hasUrlBeenUsed, setHasUrlBeenUsed] = useState<boolean>(false);
	const [selectedProtocol, setSelectedProtocol] = useState<string>('');
	const [protocolOptions, setProtocolOptions] = useState<SelectOptions<string>[]>([]);
	const [port, setPort] = useState<number>(-1);
	const [loading, setLoading] = useState<boolean>(false);
	const [disableSaveButton, setDisableSaveButton] = useState<boolean>(false);

	useEffect(() => {
		const options = protocols.map<SelectOptions<string>>((p, index) => ({
			id: index.toString(),
			label: p,
			value: p,
		}));

		let initialName: string = '';
		let initialProt: string = options[0].label;
		let initialFullUrl: string = options[0].label;
		let initialPartialUrl: string = '';

		if (editInstance) {
			initialName = editInstance.Name;
			const match = editInstance.InstanceUrl.match(urlRegexRoot);
			if (match) {
				if (match[1]) {
					initialProt = match[1];
				}
				if (match[0]) {
					initialFullUrl = match[0];
				}
				if (match[2]) {
					initialPartialUrl = match[2] + (match[30] ?? '');
				}
			} else {
				let protLength = editInstance.InstanceUrl.includes('https://') ? 8 : 7;
				initialProt = editInstance.InstanceUrl.substring(0, protLength);
				initialPartialUrl = editInstance.InstanceUrl.substring(protLength, editInstance.InstanceUrl.length);
				initialFullUrl = editInstance.InstanceUrl;
			}
		}

		batch(() => {
			setName(initialName);
			setIsNameEmpty(initialName.length === 0);
			setUrl(initialPartialUrl);
			setProtocolOptions(options);
			setSelectedProtocol(initialProt);
			setFullUrl(initialFullUrl);
			setRootUrl(initialFullUrl);
			setIsUrlValid(validateFullUrl(initialFullUrl));
		});
	}, []);

	useEffect(() => {
		if (fullUrl?.length === 0 || rootUrl?.length === 0) return;
		let urlFullReconstruct: string = fullUrl;
		let urlRootReconstruct: string = rootUrl;
		if (fullUrl.includes(protocols[1])) {
			urlFullReconstruct = selectedProtocol + fullUrl.substring(protocols[1].length);
		} else {
			urlFullReconstruct = selectedProtocol + fullUrl.substring(protocols[0].length);
		}
		if (rootUrl.includes(protocols[1])) {
			urlRootReconstruct = selectedProtocol + rootUrl.substring(protocols[1].length);
		} else {
			urlRootReconstruct = selectedProtocol + rootUrl.substring(protocols[0].length);
		}

		batch(() => {
			setFullUrl(urlFullReconstruct);
			setRootUrl(urlRootReconstruct);
		});
	}, [selectedProtocol]);

	useEffect(() => {
		const disable: boolean =
			isNameEmpty ||
			!isUrlValid ||
			port > maxPortNumber ||
			(editInstance !== undefined && editInstance.Name === name && editInstance.InstanceUrl === rootUrl);
		setDisableSaveButton(disable);
	}, [isNameEmpty, isUrlValid, name, fullUrl]);

	const validateFullUrl = (url: string): boolean => {
		return urlRegexFull.test(url);
	};

	const validateRootUrl = (url: string): boolean => {
		return urlRegexRoot.test(url);
	};

	const captureUrlParts = (url: string) => {
		const match = url.match(urlRegexFull);
		const getPortNumber = (m: RegExpMatchArray): number => {
			if (m[30]) {
				const portPart: string = m[30].toString().split(':')[1];
				const portNum = parseInt(portPart);
				if (!isNaN(portNum)) {
					return portNum;
				}
			}
			return -1;
		};

		const getRootUrl = (m: RegExpMatchArray): string => {
			if (m[1] && m[2]) {
				return m[1] + m[2] + (m[30] ?? '');
			}
			return '';
		};

		let portNum: number = -1;
		let rUrl: string = '';
		if (match) {
			portNum = getPortNumber(match);
			rUrl = getRootUrl(match);
		}

		setPort(portNum);
		setRootUrl(rUrl);
	};

	const handleOnClickSave = async () => {
		if (!isNameEmpty && isUrlValid) {
			setLoading(true);
			try {
				const instance: Instance = {
					Id: editInstance ? editInstance.Id : 0,
					Name: name.trim(),
					InstanceUrl: rootUrl.trim(),
				};
				const response: VelocityVisionResponseObject = await velocityVisionApi.isInstanceDuplicate(instance);
				if (!handleResponse(response)) {
					const { IsNameDuplicate, IsUrlDuplicate } = response;
					setIsNameDuplicate(IsNameDuplicate);
					setIsUrlDuplicate(IsUrlDuplicate);
					if (IsNameDuplicate) {
						notification['error']({
							message: _('VelocityVisionDuplicateName'),
						});
					} else if (IsUrlDuplicate) {
						notification['error']({
							message: _('VelocityVisionDuplicateUrl'),
						});
					} else {
						const res = editInstance ? await velocityVisionApi.updateInstance(instance) : await velocityVisionApi.addInstance(instance);
						if (!handleResponse(res)) {
							onConfirm();
						}
					}
				}
			} catch (err) {
				Logger.writeErrorLog(`${err.name}: ${err.message}`);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleOnClickCancel = () => {
		onCancel();
	};

	const handleNameInputChange = (value: string) => {
		batch(() => {
			setName(value);
			setIsNameEmpty(value?.length === 0);
			setIsNameDuplicate(false);
			setHasNameBeenUsed(true);
		});
	};

	const updateUrlStates = (urlInput: string) => {
		const _url: string = urlInput.replace(/[\r\n]/g, '');
		const constructedUrl = selectedProtocol + _url;
		const isValid: boolean = validateFullUrl(constructedUrl);
		const isLong: boolean = isValid && !validateRootUrl(constructedUrl);

		batch(() => {
			setUrl(_url);
			setFullUrl(constructedUrl);
			setIsUrlValid(isValid);
			setIsUrlLong(isLong);
			captureUrlParts(constructedUrl);
			setHasUrlBeenUsed(true);
		});
	};

	const handleUrlInputChange = (value: string) => {
		setIsUrlDuplicate(false);
		updateUrlStates(value);
	};

	const handleUrlInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		setIsUrlDuplicate(false);
		const doesProtocolExist = (pastedUrl: string): string => {
			for (const protocol of protocols) {
				if (pastedUrl.startsWith(protocol)) {
					return protocol;
				}
			}
			return undefined;
		};
		const input = e.target as HTMLInputElement;
		let pastedText = e.clipboardData.getData('Text');
		if (pastedText?.length > 0) {
			pastedText = pastedText.trim();
			const foundProtocol = doesProtocolExist(pastedText);
			let processedText = pastedText;

			if (foundProtocol) {
				setSelectedProtocol(foundProtocol);
				processedText = pastedText.substring(foundProtocol.length);
			}
			const start = input.selectionStart;
			const end = input.selectionEnd;
			const currentValue = input.value;
			const newValue = currentValue.slice(0, start) + processedText + currentValue.slice(end);
			input.value = newValue;
			input.setSelectionRange(start + processedText.length, start + processedText.length);
			updateUrlStates(newValue);
		}
	};

	const showNameError: boolean = (isNameEmpty && hasNameBeenUsed) || (isNameDuplicate && hasNameBeenUsed);
	const showUrlError: boolean = (!isUrlValid || port > maxPortNumber) && hasUrlBeenUsed;
	const showPortOutOfRange: boolean = port > maxPortNumber && isUrlValid;
	const showUrlWarning: boolean = isUrlLong && port <= maxPortNumber && hasUrlBeenUsed;
	const showUrlPreview: boolean = hasUrlBeenUsed && url?.length > 0;

	const urlProtocolSelect = (
		<Select
			id='addNewInstanceNameInputPrefixOptions'
			value={selectedProtocol}
			options={protocolOptions}
			onChange={(e: string) => {
				batch(() => {
					setSelectedProtocol(e);
					setHasUrlBeenUsed(true);
				});
			}}
			disabled={false}
		/>
	);

	const NameErrorSymbol = (
		<Tooltip title={isNameDuplicate ? _('DuplicateInstanceName') : _('FilterGroupErrorMessage')}>
			<ExclamationCircleOutlined />
		</Tooltip>
	);

	const UrlErrorSymbol = (
		<Tooltip title={showPortOutOfRange ? _('UrlPortErrorTooltipMessage') : _('UrlErrorTooltipMessage')}>
			<ExclamationCircleOutlined />
		</Tooltip>
	);

	const UrlWarningSymbol = (
		<Tooltip title={_('UrlWarningTooltipMessage')}>
			<InfoCircleOutlined />
		</Tooltip>
	);

	return (
		<Modal
			keyboard={true}
			maskClosable={false}
			onCancel={handleOnClickCancel}
			onClickOk={() => null}
			width={700}
			title={title}
			visible={true}
			customZoomClass={styles.WithModalZoom}
			hideCloseButton={false}
			addHelpButton={<HelpFactoryComponent helpFeature={HelpFeature.VelocityVision} topicId={topicId} size='medium' />}
			footer={[
				<div className={styles.buttonsContainer}>
					<Space>
						<Button
							id='addNewInstanceSaveButton'
							title={_('Save')}
							key='add-new-instance-save'
							type='primary'
							disabled={disableSaveButton}
							onClick={handleOnClickSave}>
							{editInstance ? _('SaveChanges') : _('Save')}
						</Button>
						<Button id='addNewInstanceCancelButton' key='add-new-instance-cancel' type='default' onClick={handleOnClickCancel} disabled={false}>
							{_('Cancel')}
						</Button>
					</Space>
				</div>,
			]}>
			<Spin tip={`${_('Loading')}...`} spinning={loading} size='large'>
				<div className={styles.container}>
					<label id='addNewInstanceNameLabel' htmlFor={nameInputId} className={styles.labelSection}>
						{_('Name')}
					</label>
					<Input
						id={nameInputId}
						key={'key-addNewInstanceNameInput'}
						value={name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNameInputChange(e.target.value)}
						maxLength={32}
						suffix={showNameError ? NameErrorSymbol : <></>}
						className={cx('', {
							[styles.error]: showNameError,
						})}
						autoComplete='off'
					/>
					<label id='addNewInstanceUrlLabel' htmlFor={urlInputId} className={styles.labelSection}>
						{_('URL')}
					</label>
					<Input
						id={urlInputId}
						key={'key-addNewInstanceUrlInput'}
						value={url}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUrlInputChange(e.target.value)}
						maxLength={200}
						addonBefore={urlProtocolSelect}
						suffix={showUrlError ? UrlErrorSymbol : showUrlWarning ? UrlWarningSymbol : <></>}
						onPaste={handleUrlInputPaste}
						spellCheck={false}
						className={cx('', {
							[styles.error]: showUrlError,
							[styles.warning]: showUrlWarning,
							[styles.error]: isUrlDuplicate,
						})}
						autoComplete='off'
					/>
					{showUrlPreview && (
						<span
							className={cx(styles.urlPreview, {
								[styles.urlPreviewError]: showUrlError,
								[styles.urlPreviewValid]: isUrlValid,
							})}>
							{isUrlValid && port <= maxPortNumber && <CheckCircleOutlined className={styles.checkMark} />}
							{showPortOutOfRange ? (
								<HighlightText text={rootUrl} highlightText={port.toString()} highlightStyle={PortErrorStyle} />
							) : (
								<p className={styles.paragraph}>{rootUrl}</p>
							)}
						</span>
					)}
				</div>
			</Spin>
		</Modal>
	);
};

export { AddEditModal };
